/* FATCUTPRESS MOSAIC — exact WebGL reference renderer, isolated for Visual Manager */
/* FATCUTPRESS WebGL Background
 * Interactive greige heat-field renderer.
 * Rendering constants are intentionally kept together in this file.
 */

(() => {
    'use strict';
    const voidBackgroundSettings = Object.assign({
      lowReaction: 1,
      highReaction: 1,
      motionSpeed: 1
    }, window.FATCUT_VOID_BACKGROUND || {});

    const canvas = document.getElementById('fatcut-bg-mosaic');
    if (!canvas) return;

    const toRatio = (value, fallback = 100) => {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed / 100 : fallback / 100;
    };

    const STYLE_ORDER = ['classic', 'soft', 'silver', 'warm', 'mosaic'];
    const STYLE_PRESETS = {
      classic: { tint: [1.000, 1.000, 1.000], speed: 1.00, pointer: 1.00, mosaic: 0.0 },
      soft:    { tint: [1.025, 1.018, 1.010], speed: 0.78, pointer: 0.82, mosaic: 0.0 },
      silver:  { tint: [0.985, 1.000, 1.022], speed: 0.92, pointer: 0.90, mosaic: 0.0 },
      warm:    { tint: [1.035, 1.005, 0.970], speed: 0.86, pointer: 0.95, mosaic: 0.0 },
      mosaic:  { tint: [0.910, 0.900, 0.885], speed: 0.94, pointer: 1.00, mosaic: 1.0 }
    };

    function resolveStyle(requested, rotation) {
      if (rotation === 'manual') return STYLE_PRESETS[requested] ? requested : 'classic';
      const now = new Date();
      const days = Math.floor(now.getTime() / 86400000);
      let index = 0;
      if (rotation === 'daily') index = days;
      if (rotation === 'weekly') index = Math.floor(days / 7);
      if (rotation === 'monthly') index = now.getFullYear() * 12 + now.getMonth();
      return STYLE_ORDER[((index % STYLE_ORDER.length) + STYLE_ORDER.length) % STYLE_ORDER.length];
    }

    /* Visual style is controlled exclusively by the Theme Editor / Visual Manager. */
    const selectedStyle = resolveStyle(
      canvas.dataset.bgStyle || 'classic',
      canvas.dataset.bgRotation || 'manual'
    );
    const preset = STYLE_PRESETS[selectedStyle];
    const config = {
      speed: toRatio(canvas.dataset.bgSpeed) * preset.speed,
      pointer: toRatio(canvas.dataset.bgPointer) * preset.pointer,
      grain: toRatio(canvas.dataset.bgGrain),
      brightness: toRatio(canvas.dataset.bgBrightness),
      tint: preset.tint,
      mosaic: preset.mosaic || 0
    };
    canvas.dataset.activeBgStyle = selectedStyle;

    const audioReactive = document.body.classList.contains('template-index');
    const audioState = { low: 0, high: 0, targetLow: 0, targetHigh: 0, playing: false };
    if (audioReactive) {
      window.addEventListener('fatcut:audio-levels', event => {
        const detail = event.detail || {};
        audioState.targetLow = Math.max(0, Math.min(1, Number(detail.low) || 0));
        audioState.targetHigh = Math.max(0, Math.min(1, Number(detail.high) || 0));
        audioState.playing = Boolean(detail.playing);
      });
    }

    const status = { textContent:'', style:{} };
    const fallback = { style:{}, textContent:'' };
    const gl = canvas.getContext('webgl', { alpha: false, antialias: false, depth: false, stencil: false, preserveDrawingBuffer: false });
    if (!gl) {
      fallback.style.display = 'grid';
      status.textContent = 'WebGL非対応';
      return;
    }

    const vert = `
      attribute vec2 a_position;
      varying vec2 v_uv;
      void main(){
        v_uv = a_position * 0.5 + 0.5;
        gl_Position = vec4(a_position, 0.0, 1.0);
      }
    `;

    const simFrag = `
      precision mediump float;
      varying vec2 v_uv;
      uniform sampler2D u_prev;
      uniform vec2 u_texel;
      uniform float u_decay;
      uniform int u_count;
      uniform vec3 u_points[10];

      void main(){
        float c = texture2D(u_prev, v_uv).r;
        float n = texture2D(u_prev, v_uv + vec2(0.0, u_texel.y)).r;
        float s = texture2D(u_prev, v_uv - vec2(0.0, u_texel.y)).r;
        float e = texture2D(u_prev, v_uv + vec2(u_texel.x, 0.0)).r;
        float w = texture2D(u_prev, v_uv - vec2(u_texel.x, 0.0)).r;
        float ne = texture2D(u_prev, v_uv + u_texel).r;
        float nw = texture2D(u_prev, v_uv + vec2(-u_texel.x, u_texel.y)).r;
        float se = texture2D(u_prev, v_uv + vec2(u_texel.x, -u_texel.y)).r;
        float sw = texture2D(u_prev, v_uv - u_texel).r;

        float heat = c * 0.28 + (n+s+e+w) * 0.12 + (ne+nw+se+sw) * 0.06;
        heat *= u_decay;

        for (int i = 0; i < 10; i++) {
          if (i >= u_count) break;
          vec2 p = u_points[i].xy;
          float strength = u_points[i].z;
          vec2 d = v_uv - p;
          d.x *= u_texel.y / u_texel.x;
          float r = dot(d, d);

          /* Compact interaction source. The persistent texture creates the trail. */
          heat += exp(-r * 1550.0) * strength;
        }

        gl_FragColor = vec4(clamp(heat, 0.0, 1.2), 0.0, 0.0, 1.0);
      }
    `;

    const drawFrag = `
      precision mediump float;
      varying vec2 v_uv;
      uniform sampler2D u_heat;
      uniform vec2 u_resolution;
      uniform float u_time;
      uniform vec2 u_seedA;
      uniform vec2 u_seedB;
      uniform float u_seedC;
      uniform vec3 u_tint;
      uniform float u_grain;
      uniform float u_brightness;
      uniform float u_audioLow;
      uniform float u_audioHigh;
      uniform vec2 u_heatTexel;
      uniform float u_leftDrift;
      uniform float u_mosaic;

      float fb(vec2 p){
        return (
          sin((p.x + u_seedA.x) * (5.2 + u_seedC * 1.9) + u_time * 0.2 + u_seedB.x * 6.2831)
          + sin((p.y + u_seedA.y) * (4.4 + u_seedC * 1.5) - u_time * 0.17 + u_seedB.y * 6.2831)
          + sin(((p.x + p.y) + u_seedB.x * 0.7) * (3.5 + u_seedC * 1.3) + u_time * 0.11 + u_seedA.x * 4.0)
        ) / 3.0;
      }

      vec3 greige(float t) {
        t = clamp(t, 0.0, 1.0);
        vec3 c0 = vec3(0.992, 0.990, 0.985); // warm white
        vec3 c1 = vec3(0.940, 0.928, 0.912); // pale greige
        vec3 c2 = vec3(0.875, 0.855, 0.830); // soft taupe
        vec3 c3 = vec3(0.775, 0.750, 0.720); // warm gray
        vec3 c4 = vec3(0.910, 0.900, 0.886); // silver gray
        vec3 c5 = vec3(1.000, 0.995, 0.985); // luminous white
        if (t < 0.18) return mix(c0, c1, t / 0.18);
        if (t < 0.42) return mix(c1, c2, (t - 0.18) / 0.24);
        if (t < 0.68) return mix(c2, c3, (t - 0.42) / 0.26);
        if (t < 0.88) return mix(c3, c4, (t - 0.68) / 0.20);
        return mix(c4, c5, (t - 0.88) / 0.12);
      }

      float noise(vec2 p) {
        return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
      }

      float liquidPatch(vec2 uv, vec2 centre, vec2 radius, float softness, float portraitMix, float screenAspect){
        vec2 d = uv - centre;

        /* v5.0 aspect correction:
           On a tall phone, normalized X covers far fewer physical pixels than
           normalized Y. Correct the distance field itself instead of trimming
           the finished blob. This widens the liquid concentration naturally,
           keeping its perceived shape close to desktop without changing tone. */
        float portraitXDistance = clamp(screenAspect, 0.42, 1.0);
        d.x *= mix(1.0, portraitXDistance, portraitMix);
        d /= radius;

        float r = dot(d, d);
        /* Wide feathering keeps every patch as dissolved concentration,
           never a hard-edged greige blob. */
        return exp(-r * softness);
      }

      float ambientField(vec2 uv){
        /* Keep the established v4.3 tone and 30-second leftward current. */
        vec2 flowingUv = uv + vec2(
          u_leftDrift,
          sin(u_leftDrift * 9.0) * 0.006
        );
        uv = flowingUv;

        /* A restrained liquid warp lets the greige concentrations melt into
           the existing field instead of reading as separate objects. */
        vec2 melt = vec2(
          fb(uv * 2.05 + vec2(u_time * 0.0105, -u_time * 0.0085)),
          fb(uv * 2.32 + vec2(-u_time * 0.0090, u_time * 0.0070))
        ) * 0.026;
        vec2 luv = uv + melt;

        /* Deliberately separated base coordinates: no two concentrations
           share the same horizontal or vertical band. Sizes range from broad
           pools to small dissolved accents, while total intensity stays close
           to v4.3 so the overall page tone does not suddenly turn greige. */
        /* Each scale has its own very slow rhythm. Large concentrations
           barely travel and breathe for a long time; smaller accents change
           sooner, avoiding one uniform mechanical tempo. */
        vec2 pLarge = vec2(
          0.16 + 0.085 * sin(u_time * 0.018 + u_seedA.x * 6.2831),
          0.24 + 0.075 * cos(u_time * 0.015 + u_seedB.y * 6.2831)
        );
        vec2 pMediumA = vec2(
          0.79 + 0.070 * cos(u_time * 0.026 + u_seedB.x * 6.2831),
          0.38 + 0.068 * sin(u_time * 0.022 + u_seedA.y * 6.2831)
        );
        vec2 pMediumB = vec2(
          0.36 + 0.078 * sin(u_time * 0.024 + u_seedC * 5.0),
          0.76 + 0.062 * cos(u_time * 0.020 + u_seedA.x * 4.0)
        );
        vec2 pSmallA = vec2(
          0.88 + 0.046 * sin(u_time * 0.041 + u_seedA.y * 5.0),
          0.82 + 0.044 * cos(u_time * 0.037 + u_seedB.x * 5.0)
        );
        vec2 pSmallB = vec2(
          0.57 + 0.052 * cos(u_time * 0.038 + u_seedB.y * 4.0),
          0.56 + 0.044 * sin(u_time * 0.032 + u_seedC * 6.0)
        );

        /* A tall phone screen makes the same normalized coordinates appear
           more column-like than they do on desktop. Blend toward a dedicated
           diagonal layout only in portrait, preserving the desktop field. */
        float screenAspect = u_resolution.x / max(u_resolution.y, 1.0);
        float portraitMix = 1.0 - smoothstep(0.72, 1.02, screenAspect);

        vec2 portraitLarge = vec2(
          0.18 + 0.055 * sin(u_time * 0.017 + u_seedA.x * 6.2831),
          0.17 + 0.050 * cos(u_time * 0.014 + u_seedB.y * 6.2831)
        );
        vec2 portraitMediumA = vec2(
          0.81 + 0.048 * cos(u_time * 0.025 + u_seedB.x * 6.2831),
          0.36 + 0.052 * sin(u_time * 0.021 + u_seedA.y * 6.2831)
        );
        vec2 portraitMediumB = vec2(
          0.27 + 0.052 * sin(u_time * 0.023 + u_seedC * 5.0),
          0.67 + 0.048 * cos(u_time * 0.019 + u_seedA.x * 4.0)
        );
        vec2 portraitSmallA = vec2(
          0.76 + 0.036 * sin(u_time * 0.040 + u_seedA.y * 5.0),
          0.86 + 0.032 * cos(u_time * 0.036 + u_seedB.x * 5.0)
        );
        vec2 portraitSmallB = vec2(
          0.56 + 0.040 * cos(u_time * 0.037 + u_seedB.y * 4.0),
          0.51 + 0.034 * sin(u_time * 0.031 + u_seedC * 6.0)
        );

        pLarge = mix(pLarge, portraitLarge, portraitMix);
        pMediumA = mix(pMediumA, portraitMediumA, portraitMix);
        pMediumB = mix(pMediumB, portraitMediumB, portraitMix);
        pSmallA = mix(pSmallA, portraitSmallA, portraitMix);
        pSmallB = mix(pSmallB, portraitSmallB, portraitMix);

        /* Slow radius breathing reads as liquid concentration spreading into
           the surrounding field rather than blobs translating across it. */
        float breatheLarge = 1.0 + 0.16 * sin(u_time * 0.010 + u_seedC * 6.2831);
        float breatheMediumA = 1.0 + 0.13 * sin(u_time * 0.017 + u_seedA.x * 5.0);
        float breatheMediumB = 1.0 + 0.12 * cos(u_time * 0.019 + u_seedB.y * 5.0);
        float breatheSmallA = 1.0 + 0.10 * sin(u_time * 0.030 + u_seedA.y * 5.0);
        float breatheSmallB = 1.0 + 0.09 * cos(u_time * 0.033 + u_seedB.x * 5.0);

        /* v5.0: shape correction now happens inside liquidPatch's distance
           field. Radii stay identical on desktop and mobile, while portrait
           X distance is compensated before the soft concentration is drawn. */
        float dissolved =
            0.066 * liquidPatch(luv, pLarge,   vec2(0.36, 0.29) * breatheLarge,      1.18, portraitMix, screenAspect)
          + 0.051 * liquidPatch(luv, pMediumA, vec2(0.225, 0.185) * breatheMediumA, 1.38, portraitMix, screenAspect)
          + 0.047 * liquidPatch(luv, pMediumB, vec2(0.195, 0.245) * breatheMediumB, 1.42, portraitMix, screenAspect)
          + 0.029 * liquidPatch(luv, pSmallA,  vec2(0.100, 0.086) * breatheSmallA,  1.62, portraitMix, screenAspect)
          + 0.026 * liquidPatch(luv, pSmallB,  vec2(0.082, 0.118) * breatheSmallB,  1.66, portraitMix, screenAspect);

        return
          0.25
          + 0.27 * fb(uv * 1.55 + vec2(
              sin(u_time * 0.085) * 1.15,
              cos(u_time * 0.068) * 1.05
            ))
          + 0.16 * fb(uv * 3.9 + vec2(
              -u_time * 0.034,
               u_time * 0.026
            ))
          + 0.07 * fb(uv * 7.2 + vec2(
               u_time * 0.019,
              -u_time * 0.022
            ))
          + dissolved;
      }

      void main(){
        /* Mosaic is a final-surface treatment only. The established liquid
           field remains intact underneath and can still be selected unchanged. */
        float screenAspect = u_resolution.x / max(u_resolution.y, 1.0);
        float mosaicColumns = mix(42.0, 64.0, smoothstep(0.55, 1.65, screenAspect));
        /* Keep desktop unchanged. On phone-shaped viewports, use roughly
           30% larger uniform tiles by reducing the grid density. */
        float mobileTileScale = mix(1.30, 1.0, smoothstep(0.78, 1.02, screenAspect));
        mosaicColumns /= mobileTileScale;
        vec2 mosaicGrid = vec2(mosaicColumns, mosaicColumns / max(screenAspect, 0.35));
        vec2 mosaicUv = (floor(v_uv * mosaicGrid) + 0.5) / mosaicGrid;
        vec2 surfaceUv = mix(v_uv, mosaicUv, u_mosaic);

        /* Interaction field is separate from the original background. */
        float touch = texture2D(u_heat, surfaceUv).r;
        float touchL = texture2D(u_heat, surfaceUv - vec2(u_heatTexel.x, 0.0)).r;
        float touchR = texture2D(u_heat, surfaceUv + vec2(u_heatTexel.x, 0.0)).r;
        float touchD = texture2D(u_heat, surfaceUv - vec2(0.0, u_heatTexel.y)).r;
        float touchU = texture2D(u_heat, surfaceUv + vec2(0.0, u_heatTexel.y)).r;
        vec2 touchGradient = vec2(touchR - touchL, touchU - touchD);
        float gradientLength = length(touchGradient);
        vec2 inwardDirection = touchGradient / max(gradientLength, 0.00001);
        float liquidMask = smoothstep(0.012, 0.32, touch);
        float liquidEdge = smoothstep(0.001, 0.075, gradientLength);

        /* Sample farther away from the high-pressure centre: the existing
           background appears to escape, like oil opening on water. */
        vec2 liquidUv = clamp(
          surfaceUv - inwardDirection * liquidEdge * liquidMask * 0.036,
          vec2(0.001), vec2(0.999)
        );

        float ambient = ambientField(liquidUv);
        float glow = smoothstep(0.01, 0.95, ambient);
        vec2 p = (gl_FragCoord.xy / u_resolution.xy) - 0.5;
        p.x *= u_resolution.x / u_resolution.y;
        float vignette = 1.0 - smoothstep(0.25, 0.95, length(p));
        float grain = (noise(gl_FragCoord.xy + u_time) - 0.5) * 0.012 * u_grain;
        float bassField = smoothstep(0.12, 0.82, glow) * u_audioLow;
        float sparkleNoise = noise(gl_FragCoord.xy * 0.53 + floor(u_time * 7.0));
        float sparkle = smoothstep(0.987, 1.0, sparkleNoise) * u_audioHigh;

        vec3 col = greige(pow(glow, 0.78));

        /* Restrained tonal stepping gives each tile its own mineral/glass
           value without turning the background into harsh digital pixel art. */
        float tonalSteps = 9.0;
        col = mix(col, floor(col * tonalSteps + 0.5) / tonalSteps, u_mosaic * 0.34);
        col *= 0.88 + 0.12 * vignette;

        /* Stronger music colour response without changing the analyser or
           playback path: lows push the mineral greige deeper and warmer,
           while highs lift it toward a brighter silver tone. */
        vec3 lowColour = col * vec3(0.43, 0.39, 0.35);
        vec3 highColour = mix(col, vec3(0.92, 0.95, 1.00), 0.62);
        col = mix(col, lowColour, bassField * 0.76);
        col = mix(col, highColour, u_audioHigh * 0.46);
        col += vec3(sparkle * 0.145);
        col += grain * 0.35;
        col += vec3(0.006,0.005,0.003);

        /* Small white contact core and a restrained oily rim. No smoke layer. */
        float whiteCore = smoothstep(0.30, 0.76, touch);
        float oilRim = liquidEdge * liquidMask * (1.0 - whiteCore * 0.45);
        col = mix(col, vec3(1.000, 0.997, 0.988), whiteCore * 0.82);
        col += vec3(oilRim * 0.085);

        col *= u_tint * u_brightness;

        /* FATCUTPRESS MOSAIC — approved 03B++ palette.
           Visual motion / pointer interaction remain unchanged. */
        float fcpLum = clamp(dot(col, vec3(0.299, 0.587, 0.114)), 0.0, 1.0);
        col = mix(
          vec3(0.115, 0.108, 0.096),
          vec3(0.80, 0.76, 0.68),
          1.0 - fcpLum
        );

        gl_FragColor = vec4(col, 1.0);
      }
    `;

    function shader(type, src) {
      const s = gl.createShader(type);
      gl.shaderSource(s, src);
      gl.compileShader(s);
      if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(s));
      return s;
    }

    function program(vs, fs) {
      const p = gl.createProgram();
      gl.attachShader(p, shader(gl.VERTEX_SHADER, vs));
      gl.attachShader(p, shader(gl.FRAGMENT_SHADER, fs));
      gl.linkProgram(p);
      if (!gl.getProgramParameter(p, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(p));
      return p;
    }

    let simProgram, drawProgram;
    try {
      simProgram = program(vert, simFrag);
      drawProgram = program(vert, drawFrag);
    } catch (err) {
      console.error(err);
      fallback.style.display = 'grid';
      fallback.textContent = 'WebGLシェーダーの起動に失敗しました。SafariまたはChromeの最新版で開いてください。';
      status.textContent = '起動失敗';
      return;
    }

    const quad = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, quad);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1,-1, 1,-1, -1,1, -1,1, 1,-1, 1,1]), gl.STATIC_DRAW);

    const SIM_SIZE = 256;
    function makeTexture() {
      const t = gl.createTexture();
      gl.bindTexture(gl.TEXTURE_2D, t);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, SIM_SIZE, SIM_SIZE, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
      return t;
    }
    function makeFbo(texture) {
      const f = gl.createFramebuffer();
      gl.bindFramebuffer(gl.FRAMEBUFFER, f);
      gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, texture, 0);
      return f;
    }

    let texA = makeTexture(), texB = makeTexture();
    let fboA = makeFbo(texA), fboB = makeFbo(texB);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);

    const interactionEnabled = document.body?.dataset.hasProducts !== 'true' && Boolean(
      document.body.classList.contains('template-index') &&
      document.querySelector('.fatcut-empty') &&
      document.querySelector('[data-void-mode]')
    );

    const points = [];
    function addPoint(x, y, strength = 0.9) {
      if (!interactionEnabled) return;
      points.push({ x, y: 1 - y, strength, born: performance.now() });
      if (points.length > 120) points.splice(0, points.length - 120);
    }

    const active = new Map();
    const interactiveSelector = 'a,button,input,textarea,select,label,[role="button"],[data-void-mode-toggle]';
    let touchLock = false;

    function isInteractiveTarget(target) {
      return target instanceof Element && Boolean(target.closest(interactiveSelector));
    }

    function posFromEvent(e) {
      const r = canvas.getBoundingClientRect();
      return {
        x: Math.max(0, Math.min(1, (e.clientX - r.left) / r.width)),
        y: Math.max(0, Math.min(1, (e.clientY - r.top) / r.height))
      };
    }

    function lockTouchPage() {
      if (touchLock) return;
      touchLock = true;
      document.documentElement.style.overscrollBehavior = 'none';
      document.body.style.touchAction = 'none';
    }

    function unlockTouchPage() {
      if (!touchLock) return;
      touchLock = false;
      document.documentElement.style.overscrollBehavior = '';
      document.body.style.touchAction = '';
    }

    if (interactionEnabled) {
      window.addEventListener('pointerdown', e => {
        if (isInteractiveTarget(e.target)) return;
        const p = posFromEvent(e);
        active.set(e.pointerId, p);
        addPoint(p.x, p.y, 0.24 * config.pointer);
        if (e.pointerType === 'touch') {
          lockTouchPage();
          e.preventDefault();
        }
      }, { passive: false });

      window.addEventListener('pointermove', e => {
        if (isInteractiveTarget(e.target)) return;
        if (e.pointerType === 'touch' && !active.has(e.pointerId)) return;

        const p = posFromEvent(e);
        const prev = active.get(e.pointerId) || p;
        const dx = p.x - prev.x;
        const dy = p.y - prev.y;
        const dist = Math.hypot(dx, dy);
        const steps = Math.max(1, Math.ceil(dist * 150));
        const strength = (e.pointerType === 'mouse' && e.buttons === 0 ? 0.135 : 0.27) * config.pointer;

        for (let i = 1; i <= steps; i++) {
          addPoint(prev.x + dx * i / steps, prev.y + dy * i / steps, strength);
        }
        active.set(e.pointerId, p);

        if (e.pointerType === 'touch') {
          lockTouchPage();
          e.preventDefault();
        }
      }, { passive: false });

      window.addEventListener('pointerenter', e => {
        if (e.pointerType !== 'mouse' || isInteractiveTarget(e.target)) return;
        const p = posFromEvent(e);
        active.set(e.pointerId, p);
        addPoint(p.x, p.y, 0.11 * config.pointer);
      }, { passive: true });

      const endInteraction = e => {
        active.delete(e.pointerId);
        if (e.pointerType === 'touch') unlockTouchPage();
      };
      window.addEventListener('pointerup', endInteraction, { passive: true });
      window.addEventListener('pointercancel', endInteraction, { passive: true });
      window.addEventListener('blur', () => {
        active.clear();
        unlockTouchPage();
      });
    }

    function resize() {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = Math.max(1, Math.floor(innerWidth * dpr));
      const h = Math.max(1, Math.floor(innerHeight * dpr));
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w; canvas.height = h;
      }
    }
    addEventListener('resize', resize, { passive: true });
    resize();

    function bindQuad(p) {
      const loc = gl.getAttribLocation(p, 'a_position');
      gl.bindBuffer(gl.ARRAY_BUFFER, quad);
      gl.enableVertexAttribArray(loc);
      gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
    }

    const fatcutBgSeedA = [Math.random(), Math.random()];
    const fatcutBgSeedB = [Math.random(), Math.random()];
    const fatcutBgSeedC = Math.random();
    const fatcutBgTimeOffset = Math.random() * 1000.0;
    let last = performance.now();
    let frames = 0, fpsTime = last;
    function frame(now) {
      resize();
      const dt = Math.min(32, now - last); last = now;

      const lowEase = audioState.targetLow > audioState.low ? 0.20 : 0.060;
      const highEase = audioState.targetHigh > audioState.high ? 0.28 : 0.11;
      audioState.low += (audioState.targetLow - audioState.low) * lowEase;
      audioState.high += (audioState.targetHigh - audioState.high) * highEase;
      if (!audioState.playing) {
        audioState.targetLow *= 0.90;
        audioState.targetHigh *= 0.82;
      }

      /* Input points are brief; the simulation texture supplies the long,
         liquid afterimage without repeatedly gathering at the cursor. */
      points.splice(0, points.findIndex(p => now - p.born < 190) < 0 ? points.length : points.findIndex(p => now - p.born < 190));
      const batch = points.slice(-10);

      gl.useProgram(simProgram);
      bindQuad(simProgram);
      gl.viewport(0, 0, SIM_SIZE, SIM_SIZE);
      gl.bindFramebuffer(gl.FRAMEBUFFER, fboB);
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, texA);
      gl.uniform1i(gl.getUniformLocation(simProgram, 'u_prev'), 0);
      gl.uniform2f(gl.getUniformLocation(simProgram, 'u_texel'), 1 / SIM_SIZE, 1 / SIM_SIZE);
      gl.uniform1f(gl.getUniformLocation(simProgram, 'u_decay'), Math.pow(0.9885, dt / 16.67));
      gl.uniform1i(gl.getUniformLocation(simProgram, 'u_count'), batch.length);
      const arr = new Float32Array(30);
      for (let i = 0; i < batch.length; i++) {
        arr[i*3] = batch[i].x;
        arr[i*3+1] = batch[i].y;
        arr[i*3+2] = batch[i].strength;
      }
      gl.uniform3fv(gl.getUniformLocation(simProgram, 'u_points[0]'), arr);
      gl.drawArrays(gl.TRIANGLES, 0, 6);

      [texA, texB] = [texB, texA];
      [fboA, fboB] = [fboB, fboA];

      gl.useProgram(drawProgram);
      bindQuad(drawProgram);
      gl.viewport(0, 0, canvas.width, canvas.height);
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, texA);
      gl.uniform1i(gl.getUniformLocation(drawProgram, 'u_heat'), 0);
      gl.uniform2f(gl.getUniformLocation(drawProgram, 'u_resolution'), canvas.width, canvas.height);
      gl.uniform1f(
        gl.getUniformLocation(drawProgram, 'u_time'),
        now * 0.001 * config.speed * voidBackgroundSettings.motionSpeed + window.scrollY * 0.00022 + fatcutBgTimeOffset
      );
      gl.uniform2f(
        gl.getUniformLocation(drawProgram, 'u_seedA'),
        fatcutBgSeedA[0],
        fatcutBgSeedA[1]
      );
      gl.uniform2f(
        gl.getUniformLocation(drawProgram, 'u_seedB'),
        fatcutBgSeedB[0],
        fatcutBgSeedB[1]
      );
      gl.uniform1f(
        gl.getUniformLocation(drawProgram, 'u_seedC'),
        fatcutBgSeedC
      );
      gl.uniform3f(
        gl.getUniformLocation(drawProgram, 'u_tint'),
        config.tint[0], config.tint[1], config.tint[2]
      );
      gl.uniform1f(gl.getUniformLocation(drawProgram, 'u_grain'), config.grain);
      gl.uniform1f(gl.getUniformLocation(drawProgram, 'u_brightness'), config.brightness);
      gl.uniform1f(gl.getUniformLocation(drawProgram, 'u_audioLow'), audioReactive ? Math.min(1, audioState.low * voidBackgroundSettings.lowReaction) : 0);
      gl.uniform1f(gl.getUniformLocation(drawProgram, 'u_audioHigh'), audioReactive ? Math.min(1, audioState.high * voidBackgroundSettings.highReaction) : 0);
      gl.uniform2f(gl.getUniformLocation(drawProgram, 'u_heatTexel'), 1 / SIM_SIZE, 1 / SIM_SIZE);
      gl.uniform1f(
        gl.getUniformLocation(drawProgram, 'u_leftDrift'),
        interactionEnabled ? now * 0.001 * 0.00367 : 0
      );
      gl.uniform1f(gl.getUniformLocation(drawProgram, 'u_mosaic'), config.mosaic);
      gl.drawArrays(gl.TRIANGLES, 0, 6);

      frames++;
      if (now - fpsTime > 1000) {
        status.textContent = `${Math.round(frames * 1000 / (now - fpsTime))} FPS`;
        frames = 0; fpsTime = now;
      }
      requestAnimationFrame(frame);
    }

    status.textContent = '起動しました';
    setTimeout(() => status.style.opacity = '.45', 1800);
    requestAnimationFrame(frame);
  
  const mosaicRegistry = window.FATCUT_VISUAL_REGISTRY;
  if (mosaicRegistry && canvas) {
    const mosaicAdapter = {
      activate() {
        canvas.hidden = false;
        canvas.style.transition = 'opacity .95s cubic-bezier(.4,0,.2,1)';
        canvas.style.opacity = '1';
      },
      deactivate() {
        canvas.style.transition = 'opacity .95s cubic-bezier(.4,0,.2,1)';
        canvas.style.opacity = '0';
      },
      update() {}
    };
    mosaicRegistry.add('mosaic', mosaicAdapter);
    mosaicRegistry.flush();
  }
})();